import type { ComponentProps, FC } from "react"

import { ClerkProvider } from "@clerk/nextjs"
import type { Theme } from "@clerk/ui/internal"
import { dark } from "@clerk/ui/themes"
import { useTheme } from "next-themes"

type ClerkThemeProviderProps = ComponentProps<typeof ClerkProvider>

/**
 * Clerk appearance variables mapped onto @coval/ui design tokens. The token CSS
 * variables (defined in packages/ui theme.css) cascade into Clerk's portals and
 * flip with the .light/.dark class, so one object themes Clerk correctly in
 * both modes: warm-greige palette, coval-contrast as the primary action color,
 * and control radius matching Button/Input (4px).
 */
const COVAL_VARIABLES: Theme["variables"] = {
  colorPrimary: "var(--coval-contrast)",
  colorBackground: "var(--surface-default)",
  colorForeground: "var(--coval-text)",
  colorMutedForeground: "var(--coval-text-muted)",
  colorInput: "var(--surface-default)",
  colorInputForeground: "var(--coval-text)",
  colorNeutral: "var(--coval-text)",
  colorDanger: "var(--coval-danger)",
  colorSuccess: "var(--coval-success-border)",
  colorWarning: "var(--coval-warning-border)",
  colorShimmer: "var(--coval-skeleton)",
  borderRadius: "var(--radius-md)",
  fontFamily: "inherit",
}

/**
 * ClerkProvider wired to next-themes and @coval/ui tokens: applies the Coval
 * appearance to all Clerk-rendered UI (org settings, user profile, org
 * switcher, auth pages) and layers Clerk's dark base theme when the app
 * resolves to dark mode. Must render inside NextThemesProvider.
 */
export const ClerkThemeProvider: FC<ClerkThemeProviderProps> = ({
  appearance,
  children,
  ...props
}) => {
  const { resolvedTheme } = useTheme()

  const mergedAppearance: Theme = {
    ...appearance,
    theme: resolvedTheme === "dark" ? dark : undefined,
    variables: { ...COVAL_VARIABLES, ...appearance?.variables },
    elements: {
      card: {
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--coval-border)",
      },
      formButtonPrimary: {
        color: "var(--coval-text-on-contrast)",
        "&:hover": { backgroundColor: "var(--coval-contrast-hover)" },
      },
      ...appearance?.elements,
      // Account deletion goes through Coval's organization and billing impact review.
      profileSection__danger: {
        display: "none",
      },
    },
  }

  return (
    <ClerkProvider {...props} appearance={mergedAppearance}>
      {children}
    </ClerkProvider>
  )
}
